import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import {
  ticketsService,
  type CreateTicketInput,
  type TicketStatus,
} from '@/services/ticketsService';

export const TICKETS_QUERY_KEY = ['tickets'] as const;

export function useTickets(tenantId?: string | null) {
  return useQuery({
    queryKey: [...TICKETS_QUERY_KEY, tenantId],
    queryFn: () => {
      if (!tenantId) throw new Error('Tenant is required');
      return ticketsService.list(tenantId);
    },
    enabled: Boolean(tenantId),
    staleTime: 15_000,
  });
}

export function useMyTickets(tenantId?: string | null, enabled = true) {
  return useQuery({
    queryKey: [...TICKETS_QUERY_KEY, 'mine', tenantId ?? null],
    queryFn: () => ticketsService.listMine(tenantId),
    enabled,
    staleTime: 15_000,
  });
}

export function useTicketDetail(id?: string | null) {
  return useQuery({
    queryKey: [...TICKETS_QUERY_KEY, 'detail', id],
    queryFn: () => ticketsService.get(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateTicket(tenantId?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTicketInput) =>
      ticketsService.create(tenantId as string, body),
    onSuccess: (t) => {
      queryClient.invalidateQueries({ queryKey: TICKETS_QUERY_KEY });
      message.success(`Sent to our ${t.team} — we'll be in touch.`);
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        "Couldn't submit your request.";
      message.error(detail);
    },
  });
}

export function useSetTicketStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: TicketStatus }) =>
      ticketsService.setStatus(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TICKETS_QUERY_KEY }),
    onError: () => message.error("Couldn't move the ticket right now."),
  });
}

export function useAssignTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, assignee }: { id: string; assignee: string }) =>
      ticketsService.assign(id, assignee),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TICKETS_QUERY_KEY }),
    onError: () => message.error("Couldn't assign the ticket."),
  });
}

export function useTicketExperts() {
  return useQuery({
    queryKey: [...TICKETS_QUERY_KEY, 'experts'],
    queryFn: () => ticketsService.experts(),
    staleTime: 300_000,
  });
}
